import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CalendarIcon, Loader2 } from "lucide-react";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { submitContact, submitDemo } from "@/lib/api";

export const INFO_OPTIONS = [
  "Boka demo",
  "Information om Redoc AML & KYC",
  "Intern kvalitetssäkring",
  "Intern kvalitetskontroll",
  "Rådgivning Penningtvättlagen",
  "Utbildning Penningtvättlagen",
  "Exempelmallar i Redoc AML & KYC",
  'Manual "Byråns rutiner enligt Penningtvättlagen"',
];

/**
 * Shared form component supporting two modes:
 *  - mode="contact"  -> Begär mer information
 *  - mode="demo"     -> Boka demo (with calendar + contact options)
 */
export default function LeadForm({
  mode = "contact",
  sourceLayout,
  theme = {},
  testidPrefix = "lead",
  showContactOptions = false,
}) {
  const isDemo = mode === "demo";
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    message: "",
  });
  const [date, setDate] = useState(undefined);
  const [infoRequest, setInfoRequest] = useState("");
  const [loading, setLoading] = useState(false);

  const handleChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Namn och e-post är obligatoriskt.");
      return;
    }
    if (showContactOptions && !infoRequest) {
      toast.error("Välj ett kontaktval.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        source_layout: sourceLayout,
      };
      if (isDemo) {
        const bokaDemo = showContactOptions ? infoRequest === "Boka demo" : true;
        await submitDemo({
          ...payload,
          preferred_date: date ? format(date, "yyyy-MM-dd") : null,
          wants_demo: bokaDemo,
          info_request: showContactOptions ? infoRequest : null,
        });
        toast.success("Tack! Vi återkommer inom kort.");
      } else {
        await submitContact(payload);
        toast.success("Tack! Vi återkommer med mer information.");
      }
      setForm({ name: "", company: "", email: "", phone: "", message: "" });
      setDate(undefined);
      setInfoRequest("");
    } catch (err) {
      const msg = err?.response?.data?.detail?.[0]?.msg || "Något gick fel. Försök igen.";
      toast.error(typeof msg === "string" ? msg : "Något gick fel. Försök igen.");
    } finally {
      setLoading(false);
    }
  };

  const inputClass = cn("w-full", theme.input || "");
  const labelClass = cn("text-sm font-medium mb-1.5 block", theme.label || "");

  return (
    <form
      onSubmit={onSubmit}
      className={cn("space-y-5", theme.form)}
      data-testid={`${testidPrefix}-form`}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor={`${testidPrefix}-name`} className={labelClass}>
            Namn *
          </Label>
          <Input
            id={`${testidPrefix}-name`}
            data-testid={`${testidPrefix}-name`}
            value={form.name}
            onChange={handleChange("name")}
            className={inputClass}
            placeholder="För- och efternamn"
            required
          />
        </div>
        <div>
          <Label htmlFor={`${testidPrefix}-company`} className={labelClass}>
            Företag / byrå
          </Label>
          <Input
            id={`${testidPrefix}-company`}
            data-testid={`${testidPrefix}-company`}
            value={form.company}
            onChange={handleChange("company")}
            className={inputClass}
            placeholder="Företagsnamn"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor={`${testidPrefix}-email`} className={labelClass}>
            E-post *
          </Label>
          <Input
            id={`${testidPrefix}-email`}
            data-testid={`${testidPrefix}-email`}
            type="email"
            value={form.email}
            onChange={handleChange("email")}
            className={inputClass}
            placeholder="namn@exempel.se"
            required
          />
        </div>
        <div>
          <Label htmlFor={`${testidPrefix}-phone`} className={labelClass}>
            Telefon
          </Label>
          <Input
            id={`${testidPrefix}-phone`}
            data-testid={`${testidPrefix}-phone`}
            value={form.phone}
            onChange={handleChange("phone")}
            className={inputClass}
            placeholder="+46 ..."
          />
        </div>
      </div>

      {showContactOptions && (
        <div className="rounded-lg border border-white/20 bg-white/5 p-4">
          <Label className={cn("mb-2 block", theme.label)}>
            Kontaktval *
          </Label>
          <Select value={infoRequest} onValueChange={setInfoRequest}>
            <SelectTrigger
              data-testid={`${testidPrefix}-info-trigger`}
              className={cn("h-10 w-full", theme.select || theme.input)}
            >
              <SelectValue placeholder="Välj kontaktval" />
            </SelectTrigger>
            <SelectContent>
              {INFO_OPTIONS.map((opt) => (
                <SelectItem key={opt} value={opt} data-testid={`${testidPrefix}-info-${opt}`}>
                  {opt}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {isDemo && (!showContactOptions || infoRequest === "Boka demo") && (
        <div>
          <Label className={labelClass}>Önskat datum för demo</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                data-testid={`${testidPrefix}-date-trigger`}
                className={cn(
                  "w-full justify-start text-left font-normal",
                  !date && "text-muted-foreground",
                  theme.dateButton
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {date ? format(date, "PPP", { locale: sv }) : "Välj ett datum"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={date}
                onSelect={setDate}
                locale={sv}
                disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>
      )}

      <div>
        <Label htmlFor={`${testidPrefix}-message`} className={labelClass}>
          {isDemo ? "Övrig information" : "Meddelande"}
        </Label>
        <Textarea
          id={`${testidPrefix}-message`}
          data-testid={`${testidPrefix}-message`}
          value={form.message}
          onChange={handleChange("message")}
          className={cn("w-full min-h-[110px]", theme.input)}
          placeholder={
            isDemo
              ? "Berätta gärna vad ni vill se under demon."
              : "Vad vill du veta mer om?"
          }
        />
      </div>

      <Button
        type="submit"
        disabled={loading}
        data-testid={`${testidPrefix}-submit`}
        className={cn("w-full md:w-auto", theme.submitButton)}
      >
        {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        {isDemo ? "Skicka förfrågan" : "Skicka förfrågan"}
      </Button>
    </form>
  );
}
